<template>
  <div :class="$style.container">
    <div :class="$style.intro">
      <TextBlock
        :content="content.text"
        :title="content.title" />
      <span>
        COVERS WITH HIGH TECH MATERIALS
        <ul :class="$style.colors">
          <li
            :class="$style.color"
            v-for="(cover, i) in content.covers"
            :key="cover.color"
            v-if="i !== 0"
            @click="goTo(i)"
          >
            <div :style="{ backgroundImage: `url(${cover.color}` }"></div>
          </li>
        </ul>
      </span>
    </div>
    <ul :class="$style.slider" ref="slider" v-lazy:background-image="content.covers[0].img">
      <li v-for="cover in content.covers" :key="cover.img" :class="$style.slide" data-slide>
        <div v-lazy:background-image="cover.img"></div>
      </li>
    </ul>
    <div :class="$style.navContainer" data-anim="fade">
      <div :class="$style.navigator" ref="navigator">
        <div :class="$style.filler" ref="filler" :style="{ transform: counterSize }"></div>
      </div>
      <ArrowButton :action="next" :direction="'right'" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import gsap from 'gsap'

import ArrowButton from '@/components/ArrowButton'
import TextBlock from '@/components/TextBlock'

export default {
  props: ['content'],

  components: {
    TextBlock,
    ArrowButton
  },

  data() {
    return {
      current: 0,
      currentSlide: null,
      oldSlide: null,
      isInTransition: false,
    }
  },

  computed: {
    ...mapGetters(['getScrollbar']),

    counterSize() {
      const size = 1 / (this.content.covers.length / (this.current + 1))
      return `scaleX(${size})`
    }
  },

  mounted() {
    gsap.set('[data-slide]', { xPercent: 100 })
    this.oldSlide = this.$el.querySelectorAll('[data-slide]')[0]
    gsap.set(this.oldSlide, { xPercent: 0 })
    this.handlerNext = () =>

    this.$nextTick(() => {
      this.getScrollbar.addListener(this.customScrollbar.bind(this))
    })
  },

  methods: {
    customScrollbar() {
      const visible = this.getScrollbar.isVisible(this.$el)

      if (visible && !this.isPlaying) {
        this.isPlaying = true
        this.interval = setInterval(this.next, 4000)
      } else if (!visible && this.interval) {
        this.isPlaying = false
        window.clearInterval(this.interval)
      }
    },

    next() {
      this.isInTransition = true;

      const tl = new gsap.TimelineMax({ onComplete: () => { this.isInTransition = false }})
      this.oldSlide = this.$el.querySelectorAll('[data-slide]')[this.current]
      this.current = this.current >= (this.content.covers.length - 1) ? 1 : this.current + 1
      this.currentSlide = this.$el.querySelectorAll('[data-slide]')[this.current]

      window.clearInterval(this.interval)

      tl
        .fromTo(this.currentSlide, 1.4, {
          xPercent: 100,
          zIndex: 2
        }, {
          zIndex: 2,
          xPercent: 0,
          ease: gsap.Power3.easeOut,
          clearProps: 'z-index'
        })
        .set(this.oldSlide, {
          zIndex: 0,
          xPercent: 100
        })

      this.interval = setInterval(this.next, 4000)
    },

    goTo(i) {
      if (i === this.current) return;
      if (this.isInTransition) return;
      this.isInTransition = true;

      const tl = new gsap.TimelineMax({ onComplete: () => { this.isInTransition = false }})
      this.oldSlide = this.$el.querySelectorAll('[data-slide]')[this.current]
      this.current = i
      this.currentSlide = this.$el.querySelectorAll('[data-slide]')[this.current]

      window.clearInterval(this.interval)

      tl
        .fromTo(this.currentSlide, 1.4, {
          xPercent: 100,
          zIndex: 2
        }, {
          zIndex: 2,
          xPercent: 0,
          ease: gsap.Power3.easeOut,
          clearProps: 'z-index'
        })
        .set(this.oldSlide, {
          zIndex: 0,
          xPercent: 100
        })

      this.interval = setInterval(this.next, 4000)
    }
  }
}
</script>

<style module lang="scss">
@import "~sass-mq/mq";

.container {
  @include grid-size();
  padding: $big-margin-mobile;
  padding-right: 0;
  display: flex;
  flex-flow: column;
  align-items: flex-end;

  @include mq(sm) {
    padding: $big-margin;
  }
}

.slider {
  position: relative;
  margin: $big-margin-mobile 0;
  width: 100%;
  height: 80px;
  overflow: hidden;
  max-width: 800px;

  background-position: center right;
  background-size: contain;
  background-repeat: no-repeat;

  @include mq(sm) {
    margin: $big-margin 0;
    height: 150px;
  }
}

.background {
  height: 100%;
}

.slide {
  width: 100%;
  height: 100%;
  display: block;
  position: absolute;
  z-index: 1;

  > div {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-position: right center;
    background-size: contain;
    background-repeat: no-repeat;
  }
}

.colors {
  position: relative;
  width: 100%;
  margin-top: 20px;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  > li {
    position: relative;
    width: 20px;
    height: 20px;
    margin-left: 10px;

    border-radius: 100%;
    background-color: $color-ui;

    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;

    > div {
      width: 16px;
      height: 16px;
      border-radius: 100%;
      background-position: center center;
      background-size: cover;
      background-repeat: no-repeat;
    }
  }
}

.intro {
  display: flex;
  width: 100%;
  align-items: flex-end;
  flex-wrap: wrap;

  @include mq(sm) {
    flex-wrap: nowrap;
    justify-content: space-between;
    padding-right: $big-margin;
  }

  span {
    width: 50%;
    font-size: 12px;
    font-weight: normal;
    font-style: normal;
    letter-spacing: 2.5px;
    margin-top: $small-margin-mobile;
    color: $color-gray-brown;

    @include mq(sm) {
      text-align: right;
    }
  }
}

.navContainer {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 $standard-margin-mobile;

  @include mq(sm) {
    width: 30%;
  }
}

.navigator {
  position: relative;
  width: 100%;
  height: 2px;
  background: $color-dark;
  margin-right: $big-margin-mobile;

  @include mq(sm) {
    margin-right: $big-margin;
  }

  .filler {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background: $color-blue;
    transform-origin: 0;
    transition: all 0.55s ease-out;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Covers.vue